/**
 * Project Chroma on the web.
 *
 * The catalog is DATA, never authored here (`docs/theme/theme-catalog-as-data-2026-09.md`):
 * `shared/themes/catalog.json` → `./gradlew :shared:exportThemeTokens` → `web/tokens/themes.json`
 * (the bundled snapshot) and `web/tokens/chroma.css` (one `[data-chroma-theme="…"]` block per
 * theme, the pre-JS first-paint layer). The same documents are published to the object plane
 * (`THEME_CATALOG_URL`); {@link loadThemeCatalog} fetches them after first paint and, when they
 * validate, REPLACES the list, so adding or removing a theme needs no build. `applyTheme` writes the
 * theme's `--chroma-*` variables through CSSOM (`theme-document.ts`), so a theme that exists only
 * in the fetched catalog renders fully; `styles.css` reads only `var(--chroma-*)`.
 *
 * This module derives the offered list and its families from the catalog, decides which theme to
 * start in, and applies it. There is no theme id and no colour literal in this file beyond the two
 * defaults — a hand-written list would silently drift from the catalog the day a theme is added.
 */

import snapshot from "../../../tokens/themes.json";
import collection from "../../../../shared/themes/web-collection.json";
import { THEME_CATALOG_URL } from "../config.js";
import { cssColor, familyOf, parseThemeCatalog, themeCssVariables, type ThemeDocument } from "./theme-document.js";

export type { ThemeDocument } from "./theme-document.js";

export interface ThemeOption {
  id: string;
  name: string;
  /** The family this theme groups under: its base's id (its own id on a base). */
  familyId: string;
  /** The family's name — its base theme's name. */
  familyName: string;
  /** The label inside the family ("Garden Jade"); empty on the base. */
  variantName: string;
  /** "Family · Variant", or the family name on a base: what the header picker announces. */
  label: string;
  /** Dark enough that form controls should render in their dark form. */
  dark: boolean;
  /** Minimal palettes may deliberately export identical board square colours. */
  monochromeBoard: boolean;
  /** Board-coordinate text for the light and the dark squares ({@link coordinateInks}). */
  coordinateInk: { onLight: string; onDark: string };
  /**
   * The catalog's header face (`sensory.headerFont`: "serif", "system", …). It drives the display
   * role (owner ruling 2026-09-25); `applyTheme` exposes it as `data-chroma-header-font` so the
   * stylesheet can put the Source Serif stand-in in front of a serif face only (styles.css).
   */
  headerFont: string;
}

/**
 * Luminance of the theme's own page background, on the sRGB relative scale.
 * The catalog stores `#RRGGBB`; Android's 8-digit `#AARRGGBB` appears on the
 * highlight colours but not on the chrome backgrounds — it is handled anyway so
 * a future export cannot make this silently return 0 (black) for a light theme.
 */
function luminance(hex: string): number {
  const raw = hex.replace("#", "");
  const rgb = raw.length === 8 ? raw.slice(2) : raw;
  const value = Number.parseInt(rgb, 16);
  if (!Number.isFinite(value) || rgb.length !== 6) return 1;
  const channel = (shift: number): number => {
    const c = ((value >> shift) & 0xff) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
}

/** WCAG contrast of two catalog colours (`#RRGGBB`, or Android's `#AARRGGBB` read opaque). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** sRGB → CIELAB (D65), for {@link colourDifference}. */
function lab(hex: string): [number, number, number] {
  const raw = hex.replace("#", "");
  const six = raw.length === 8 ? raw.slice(2) : raw;
  const value = Number.parseInt(six, 16);
  const [r, g, b] = [16, 8, 0].map((shift) => {
    const c = ((value >> shift) & 0xff) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const f = (t: number): number => (t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116);
  const x = f((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
  const y = f(0.2126 * r + 0.7152 * g + 0.0722 * b);
  const z = f((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}

/**
 * CIE76 ΔE of two catalog colours: how different they LOOK, hue included. The best-move arrow's
 * test uses it — a saturated arrow on a square of similar lightness has a WCAG ratio near 1 yet
 * reads plainly (`contrastRatio` is luminance only). About 2.3 is a just-noticeable difference.
 */
export function colourDifference(a: string, b: string): number {
  const [x, y] = [lab(a), lab(b)];
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

/** The catalog fields {@link coordinateInks} reads. */
interface CoordinatePalette {
  colors: { lightSquare: string; darkSquare: string; coordinateColor: string };
  chrome: { textPrimary: string; textTertiary: string; backgroundPrimary: string };
}

/**
 * Board coordinates are 11 px TEXT on a square (WCAG 1.4.3, design review 2026-09-23: 3.2:1),
 * and one colour cannot clear 4.5:1 on both a light and a dark square — so each square colour
 * gets its own ink, derived here from the theme's own palette rather than set per element: the
 * theme's coordinate colour if it clears, else the first of its ink/paper/square colours that
 * does, else black or white. A monochrome board draws its dark squares in text-tertiary
 * (`immersive.css`), so that is the paper there.
 */
export function coordinateInks(theme: CoordinatePalette): { onLight: string; onDark: string } {
  const monochrome = theme.colors.lightSquare.toUpperCase() === theme.colors.darkSquare.toUpperCase();
  const darkSquare = monochrome ? theme.chrome.textTertiary : theme.colors.darkSquare;
  const candidates = [theme.colors.coordinateColor, theme.chrome.textPrimary, theme.chrome.backgroundPrimary, theme.colors.lightSquare, theme.colors.darkSquare, "#000000", "#FFFFFF"];
  const inkOn = (paper: string): string =>
    candidates.find((ink) => contrastRatio(ink, paper) >= 4.5)
    ?? candidates.reduce((best, ink) => (contrastRatio(ink, paper) > contrastRatio(best, paper) ? ink : best));
  return { onLight: inkOn(theme.colors.lightSquare), onDark: inkOn(darkSquare) };
}


/** A family in picker order: its base first (when listed), then its variants in catalog order. */
export interface ThemeFamily {
  id: string;
  name: string;
  themes: readonly ThemeOption[];
}

function optionsOf(documents: readonly ThemeDocument[]): ThemeOption[] {
  const names = new Map(documents.map((theme) => [theme.id, theme.name]));
  return documents.map((theme) => {
    const familyId = familyOf(theme);
    const familyName = names.get(familyId) ?? theme.name;
    const variantName = familyId === theme.id ? "" : theme.variantName;
    return {
      id: theme.id, name: theme.name, familyId, familyName, variantName,
      label: variantName ? `${familyName} · ${variantName}` : theme.name,
      dark: luminance(theme.chrome.backgroundPrimary) < 0.5,
      monochromeBoard: theme.colors.lightSquare.toUpperCase() === theme.colors.darkSquare.toUpperCase(),
      coordinateInk: coordinateInks(theme), headerFont: theme.sensory.headerFont,
    };
  });
}

/**
 * The themes grouped by family, families in first-seen catalog order (`§2`: array order is picker
 * order). The catalog keeps a family contiguous and its base first; grouping here does not rely on it.
 */
export function themeFamilies(themes: readonly ThemeOption[] = THEMES): ThemeFamily[] {
  const families = new Map<string, ThemeOption[]>();
  for (const theme of themes) {
    const members = families.get(theme.familyId) ?? [];
    if (theme.id === theme.familyId) members.unshift(theme); else members.push(theme);
    families.set(theme.familyId, members);
  }
  return [...families].map(([id, members]) => ({ id, name: members[0]?.familyName ?? id, themes: members }));
}

// ---------------------------------------------------------------------------------------------
// The live catalog: the bundled snapshot first, then whatever `loadThemeCatalog` validates.
// ---------------------------------------------------------------------------------------------

let documents: readonly ThemeDocument[] = [];
let documentsById = new Map<string, ThemeDocument>();
let byId = new Map<string, ThemeOption>();
/** Every id → family ever listed, so a stored id that a new catalog removed still finds its family. */
const familyMemory = new Map<string, string>();
const catalogListeners = new Set<() => void>();

/**
 * The catalog, in catalog order — the pickers' only source of ids. A LIVE binding: a fetched
 * catalog replaces it (read it when you need it; do not keep a copy), and {@link onThemesChange}
 * says when.
 */
export let THEMES: readonly ThemeOption[] = [];

// The web collection uses the shared schema, but ships with the artwork in this build.
// It is deliberately not published to the native runtime catalog before native selection.
// Remote documents replace the ordinary roster; matching published IDs take precedence
// when a collection theme is eventually promoted. Removing a collection entry is a release.
const collectionDocuments = parseThemeCatalog(collection);
function withCollection(next: readonly ThemeDocument[]): readonly ThemeDocument[] {
  const ids = new Set(next.map(theme => theme.id));
  return [...next, ...collectionDocuments.filter(theme => !ids.has(theme.id))];
}

function install(next: readonly ThemeDocument[]): void {
  next = withCollection(next);
  documents = next;
  documentsById = new Map(next.map((theme) => [theme.id, theme]));
  THEMES = optionsOf(next);
  byId = new Map(THEMES.map((t) => [t.id, t]));
  for (const theme of THEMES) familyMemory.set(theme.id, theme.familyId);
}
/**
 * The last catalog the fetch validated, kept so a theme that exists only in the published catalog
 * paints on the NEXT visit's first frame instead of flashing its family base (the app keeps the
 * same cache on disk). The bundled snapshot is the floor: an absent, blocked or unusable cache
 * falls through to it, and the fetch replaces either.
 */
const CATALOG_CACHE_KEY = "chroma-catalog";

/** FNV-1a (32-bit) of a string, as 8 hex digits: a stable identity, not a security hash. */
export function stableHash(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * The identity of the bundled snapshot (§2.4 "Cache"): a cache written under another build's
 * snapshot is discarded, so a stale cache never outlives the release that shipped a newer one.
 */
export const SNAPSHOT_ID = stableHash(JSON.stringify([snapshot, collection]));

function readCachedCatalog(): readonly ThemeDocument[] {
  try {
    const raw = globalThis.localStorage?.getItem(CATALOG_CACHE_KEY);
    if (!raw) return [];
    const cached = JSON.parse(raw) as { snapshot?: unknown; catalog?: unknown } | null;
    return cached?.snapshot === SNAPSHOT_ID ? parseThemeCatalog(cached.catalog) : [];
  } catch {
    return [];
  }
}

function cacheCatalog(body: unknown): void {
  try {
    globalThis.localStorage?.setItem(CATALOG_CACHE_KEY, JSON.stringify({ snapshot: SNAPSHOT_ID, catalog: body }));
  } catch {
    /* Blocked or full storage: the next visit starts from the snapshot, as before. */
  }
}

const cachedCatalog = readCachedCatalog();
install(cachedCatalog.length > 0 ? cachedCatalog : parseThemeCatalog(snapshot));

export const isKnownTheme = (id: string | null | undefined): boolean => (id ? byId.has(id) : false);

/** The listed document for `id`, or undefined (unknown, or removed by a fetched catalog). */
export function themeDocument(id: string | null | undefined): ThemeDocument | undefined {
  return id ? documentsById.get(id) : undefined;
}

/** These original 2D families have no matching geometry in the dimensional renderer yet. */
export function themeSupports3d(id: string): boolean {
  const prefix = themeDocument(id)?.pieceConfig.pieceSetPrefix;
  return prefix !== 'at' && prefix !== 'sg' && prefix !== 'zj' && prefix !== 'ast';
}

/** The document on screen, else the catalog's first: for renderers that must always have colours. */
export function activeThemeDocument(): ThemeDocument | undefined {
  const id = typeof document === "undefined" ? active : document.documentElement.getAttribute("data-chroma-theme");
  return themeDocument(id) ?? documents[0];
}

/** "Family · Variant" (or the family name on a base) for a listed id; the id itself otherwise. */
export const themeLabel = (id: string): string => byId.get(id)?.label ?? id;

/** Called after a fetched catalog replaced the list (and before the active theme is re-applied). */
export function onThemesChange(listener: () => void): () => void {
  catalogListeners.add(listener);
  return () => { catalogListeners.delete(listener); };
}

/**
 * The two entry points. Light is the site's own theme — the one the CSS was
 * designed in and the one the iOS app ships. Dark is `midnight`: themes.json
 * carries no pairing field (there is no "dark twin" concept in the catalog), so
 * the twin is named here, once, and the test pins that it exists and is dark.
 */
export const DEFAULT_LIGHT_THEME = "zen_chess";
export const DEFAULT_DARK_THEME = "midnight";

export interface ThemeInputs {
  /** The persisted choice, or null when the visitor has never chosen. */
  stored: string | null;
  /** The persisted choice's family (`chroma-theme-family`), stored beside it. */
  storedFamily?: string | null;
  /** `prefers-color-scheme: dark`. */
  prefersDark: boolean;
}

/**
 * Which theme to render (§2.4 "Stored choice"). A stored choice always wins — it is an explicit
 * act — if the catalog still lists it; a removed one falls back to its family's base (the stored
 * family, persisted beside the id so it survives the visit after the removal), then the system
 * preference decides, then the catalog's first theme (a published catalog need not keep the
 * defaults). Pure over the current list, so testable without a browser.
 */
export function resolveTheme({ stored, storedFamily, prefersDark }: ThemeInputs): string {
  if (stored && byId.has(stored)) return stored;
  const family = storedFamily || (stored ? familyMemory.get(stored) : undefined);
  if (family && byId.has(family)) return family;
  const preferred = prefersDark ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
  return byId.has(preferred) ? preferred : THEMES[0]?.id ?? preferred;
}

const STORAGE_KEY = "chroma-theme";
const FAMILY_STORAGE_KEY = "chroma-theme-family";

/**
 * The visitor's committed choice this page load, kept in memory too: with storage blocked it is
 * still the choice a replaced catalog re-resolves from (never the theme merely on screen).
 */
let chosen: { id: string; family: string | null } | null = null;

const readKey = (key: string): string | null => {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null; // localStorage throws in private mode and under a blocked-cookies policy.
  }
};

export function readStoredTheme(): string | null {
  return readKey(STORAGE_KEY) ?? chosen?.id ?? null;
}

/** The stored choice's family, written with it by {@link storeTheme}. */
export function readStoredFamily(): string | null {
  return readKey(FAMILY_STORAGE_KEY) ?? chosen?.family ?? null;
}

export function storeTheme(id: string): void {
  const family = byId.get(id)?.familyId ?? familyMemory.get(id) ?? null;
  chosen = { id, family };
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, id);
    if (family) globalThis.localStorage?.setItem(FAMILY_STORAGE_KEY, family);
    else globalThis.localStorage?.removeItem?.(FAMILY_STORAGE_KEY);
  } catch {
    /* A visitor who blocks storage still gets the theme for this page load. */
  }
}

/** Where the stored choice resolves in the current list. */
const resolveChoice = (): string =>
  resolveTheme({ stored: readStoredTheme(), storedFamily: readStoredFamily(), prefersDark: prefersDarkScheme() });

let active: string | null = null;
const themeListeners = new Set<(id: string) => void>();

/** Renderers subscribe when loaded, so selecting a homepage theme needs no board assets. */
export function onThemeChange(listener: (id: string) => void): () => void {
  themeListeners.add(listener);
  return () => { themeListeners.delete(listener); };
}

/** The `--chroma-*` names `applyTheme` owns on <html> (the exporter's fixed set). */
const VARIABLE_NAMES = (documents[0] ? themeCssVariables(documents[0]) : []).map(([name]) => name);

/**
 * Apply a theme to the document. `color-scheme` follows the catalog's own
 * darkness so the browser's form controls, scrollbars and focus rings match the
 * palette instead of staying stubbornly light on `midnight`. The `--chroma-*`
 * variables come from the theme's document (CSSOM assignment, never a style=
 * attribute — the CSP is `style-src 'self'`); an unlisted id clears them so its
 * `chroma.css` block, if any, shows through.
 */
export function applyTheme(id: string): void {
  const root = document.documentElement;
  const theme = byId.get(id);
  const doc = documentsById.get(id);
  root.setAttribute("data-chroma-theme", id);
  root.setAttribute('data-chroma-monochrome-board', String(theme?.monochromeBoard ?? false));
  root.setAttribute("data-chroma-header-font", theme?.headerFont ?? "");
  if (doc) for (const [name, value] of themeCssVariables(doc)) root.style.setProperty(name, value);
  else for (const name of VARIABLE_NAMES) root.style.removeProperty?.(name);
  root.style.setProperty("color-scheme", theme?.dark ? "dark" : "light");
  const inks = theme?.coordinateInk;
  if (inks) {
    root.style.setProperty("--coord-on-light", inks.onLight);
    root.style.setProperty("--coord-on-dark", inks.onDark);
  }
  if (doc) setBrowserThemeColour(cssColor(doc.chrome.backgroundPrimary));
  active = id;
  for (const listener of themeListeners) listener(id);
}

/**
 * The browser's own chrome (mobile address bar, PWA title bar) follows the theme: `<meta
 * name="theme-color">` takes the page background. Created if the HTML has none; a static value in
 * the HTML is only the pre-JS default.
 */
function setBrowserThemeColour(colour: string): void {
  if (typeof document.querySelector !== "function") return;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    if (!document.head) return;
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.append(meta);
  }
  meta.setAttribute("content", colour);
}

/**
 * A theme shown but not kept (a ⌘K row under the highlight). Remembered so a catalog that lands
 * mid-preview re-applies the preview rather than snapping back to the stored choice; an id the
 * list no longer has shows the stored choice instead. Showing the resolved choice again (Escape)
 * ends the preview, as does {@link commitTheme}.
 */
let previewing: string | null = null;

export function previewTheme(id: string): void {
  const shown = byId.has(id) ? id : resolveChoice();
  previewing = shown === resolveChoice() ? null : shown;
  applyTheme(shown);
}

/** Keep a theme (Enter, the View menu): apply and persist; any preview ends. */
export function commitTheme(id: string): void {
  previewing = null;
  applyTheme(id);
  storeTheme(id);
}

/**
 * What is on screen right now. Held in memory rather than re-derived, so a
 * picker rendered after a route change still shows the visitor's choice even
 * when localStorage is unavailable and the choice could not be persisted.
 */
export function currentTheme(): string {
  return active ?? initTheme();
}

/** Read the system preference, defaulting to light where `matchMedia` is absent. */
export function prefersDarkScheme(): boolean {
  return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

/**
 * Boot: resolve and apply before the first render. Called at the top of
 * `main.ts` — no inline <script>, because the site's CSP is a bare
 * `script-src 'self'` with no `unsafe-inline`.
 */
export function initTheme(): string {
  const id = resolveChoice();
  applyTheme(id);
  return id;
}

/**
 * Replace the list with a fetched catalog (a JSON array of theme documents). Documents that fail
 * validation are skipped one by one (`parseThemeCatalog`); if none survives the list is kept and
 * this returns false. Replace, never merge, so a theme removed from the catalog leaves the pickers.
 * The theme on screen is then re-resolved from the stored choice — the choice if it is still
 * listed, else its family base, else the default — and re-applied, since its colours may have
 * changed; a ⌘K preview that is still listed stays on screen instead.
 */
export function replaceThemes(body: unknown): boolean {
  const next = parseThemeCatalog(body);
  if (next.length === 0) return false;
  if (JSON.stringify(withCollection(next)) === JSON.stringify(documents)) return true;
  install(next);
  for (const listener of catalogListeners) listener();
  if (active === null) return true;
  if (previewing !== null && byId.has(previewing)) applyTheme(previewing);
  else {
    previewing = null;
    applyTheme(resolveChoice());
  }
  return true;
}

/**
 * After first paint: fetch the published catalog and replace the list with it. Revalidates rather
 * than trusting a stale HTTP cache (the object carries `max-age 300`); any failure — offline, 404,
 * CORS, bad JSON, nothing valid — keeps the bundled snapshot, silently. Resolves true on a replace.
 */
export async function loadThemeCatalog(url: string = THEME_CATALOG_URL, fetcher: typeof fetch = globalThis.fetch): Promise<boolean> {
  try {
    const response = await fetcher(url, { cache: "no-cache", credentials: "omit" });
    if (!response.ok) return false;
    const body: unknown = await response.json();
    const replaced = replaceThemes(body);
    if (replaced) cacheCatalog(body);
    return replaced;
  } catch {
    return false;
  }
}
